// components/purchase-orders/PurchaseOrderForm.jsx
//
// The SaaS authoring surface for Purchase Orders. Three modes, selected by
// which prop the caller passes: `recommendationId` (+ optional
// `excludedLineIds`) pre-fills from a recommendation, `purchaseOrderId` opens
// an existing draft, neither creates from scratch.
//
// All non-JSX logic lives in `usePurchaseOrderDraft`. This file is the MUI +
// AG Grid rendering layer over that hook's state.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, IconButton, Select, MenuItem,
  FormControl, InputLabel, Typography, Alert, Box,
  FormControlLabel, Checkbox, Stack,
  Accordion, AccordionSummary, AccordionDetails, Tooltip,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { ICON, CaretDown, Plus, Trash, X } from '../../icons';
import { useFormatters } from '../../hooks/useFormatters';
import { useAgGridTheme, ROW_HEIGHT } from '../../utils/agGridTheme';
import { defaultColDef, defaultGridProps, editableGridProps, copyOnlyContextMenuItems, iconActionColumn, flexColumn } from '../../utils/gridDefaults';
import { useColumnTypes } from '../../hooks/useColumnTypes';
import {
  usePurchaseOrderDraft, lineExtendedTotal, describeSuggestion, pickerRowFromLine,
  brokenOrderRules, SUGGEST_SCOPES, SALES_RATE_WINDOW_DAYS,
} from '../../hooks/usePurchaseOrderDraft';
import { useWriteGate } from '../../hooks/useWriteGate';
import StatusChip from '../common/StatusChip';
import AppDatePicker from '../common/AppDatePicker';
import { useStoreToday } from '../../hooks/useStoreToday';
import DialogErrorBanner from '../common/DialogErrorBanner';
import ConfirmDialog from '../common/ConfirmDialog';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ItemPickerDialog from '../common/ItemPickerDialog';
import PoCostQuestionDialog from './PoCostQuestionDialog';
import { useIsShopify, itemIdentityColumns } from '../grid/productIdentity';
import GridIconAction from '../grid/GridIconAction';

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------
//
//   open                — controls dialog visibility
//   onClose             — close handler
//   onSaved({ id, poNumber, status, messages }) — called on successful save;
//                         `id` lets the caller open the PO's details.
//   recommendationId    — optional. When set, the form pre-fills from the
//                         recommendation via /from_recommendation/.
//   excludedLineIds     — optional list of rec line IDs the merchant
//                         unchecked before opening the form. The hook keys its
//                         pre-fill on the VALUE of this list, not the array's
//                         identity, so an inline literal at the call site is
//                         safe (see the hook's `excludedLineIdsKey`).
//   purchaseOrderId     — optional. When set, the form opens in edit mode
//                         against an existing draft PO: the hook fetches
//                         /draft_payload/ and POSTs /update_draft/ on save.
//                         Mutually exclusive with recommendationId; neither =
//                         create from scratch.
//   onRecommendationGone — optional. Fired instead of an in-form error when
//                         the rec pre-fill 404s: the recommendation's run was
//                         replaced by a newer calculation (ids are per-run).
//                         The caller closes the form and refreshes its list.
// -----------------------------------------------------------------------------

// Supplier SKU cell on the lines. No cap here: the bound is the column's own,
// enforced once at save (PurchaseOrderCreateSerializer.supplier_sku) — an
// over-long SKU is a field error in the banner, never a shortened code.
const setSupplierSku = (params) => {
  params.data.supplier_sku = (params.newValue ?? '').toString();
  return true;
};

// The supplier's order rules for the item, one column each, under the name and
// header tooltip Supplier catalog gives them (only Purchase pack size needs
// one). The lines show them just before the quantity they bound, so the
// merchant reads the rule beside the number that breaks it. The picker does
// not: its Order qty cell carries the warning instead (orderRuleWarning), and
// the rules are read here once the item is added.
const orderRuleColumns = (t) => [
  {
    field: 'moq',
    headerName: t('supplierCatalog.columns.moq'),
    type: 'numberColumn',
  },
  {
    field: 'max_oq',
    headerName: t('supplierCatalog.columns.maxOq'),
    type: 'numberColumn',
  },
  {
    field: 'order_multiple',
    headerName: t('supplierCatalog.columns.orderMultiple'),
    headerTooltip: t('supplierCatalog.tooltips.orderMultiple'),
    type: 'numberColumn',
  },
];

// The sentence the quantity cell's tooltip says for each broken order rule
// (brokenOrderRules).
const ORDER_RULE_TOOLTIP = {
  moq: 'purchaseOrders.create.underMoqTooltip',
  max_oq: 'purchaseOrders.create.overMaxOqTooltip',
  order_multiple: 'purchaseOrders.create.offOrderMultipleTooltip',
};

// Amber on a quantity cell whose value breaks one of the supplier's order
// rules, with a hover that names each rule it breaks and the rule's limit —
// in the picker the one place a limit is shown, on the lines the sentence
// beside the rule columns. `lineOf` reads the row in the line's names
// (brokenOrderRules); the picker's quantity lives in `_draftQty`.
const orderRuleWarning = (t, formatNumber, lineOf = (row) => row) => ({
  cellClassRules: {
    'ag-cell-attention-warning': (params) => brokenOrderRules(lineOf(params.data)).length > 0,
  },
  tooltipValueGetter: (params) => {
    const sentences = brokenOrderRules(lineOf(params.data)).map(({ rule, value }) => (
      t(ORDER_RULE_TOOLTIP[rule], { value: formatNumber(value, 0) })
    ));
    return sentences.length ? sentences.join(' ') : null;
  },
});

function PurchaseOrderForm({ open, onClose, onSaved, recommendationId = null, excludedLineIds = [], purchaseOrderId = null, onRecommendationGone = null }) {
  const { t } = useTranslation();
  const isShopify = useIsShopify();
  // While an ERP owns the PO lifecycle (PO_FINAL = ERP) there is no Manexys
  // draft stage — the single action creates the PO straight in the ERP (Open)
  // and the backend rejects a 'draft' create. So the form offers only "Create
  // in Exact"; "Save as Draft" is hidden.
  //
  // THREE states, not two: the save actions are withheld until the owner of
  // this PO's lifecycle is known, and a failed read says why.
  const poGate = useWriteGate('po_final');
  const erpManaged = poGate.erpVendor !== null;
  const erpModeUnknown = poGate.failed;
  const erpModePending = !poGate.mayWrite && !erpManaged && !erpModeUnknown;
  const { formatNumber, parseNumber, formatCurrency, formatDate } = useFormatters();
  const storeToday = useStoreToday();
  const columnTypes = useColumnTypes();
  const gridTheme = useAgGridTheme();
  const selectedGridRef = useRef(null);
  // Stable identity — see ForecastGrid's getRowId for the AG Grid rationale.
  const getSelectedLineRowId = useCallback(
    (params) => String(params.data.item_id),
    [],
  );

  // All state, effects, fetch logic, totals and the save handler live in the
  // hook. This component is the MUI/AG-Grid rendering layer only.
  const { state, actions, derived } = usePurchaseOrderDraft({
    open,
    recommendationId,
    excludedLineIds,
    purchaseOrderId,
    onSaved,
    onClose,
    onRecommendationGone,
  });

  const {
    supplierId, locationId, needByDate, needByDateError,
    notes, supplierNotes, referenceNumber,
    taxInclusive, selectedLines,
    suppliers, suppliersLoading, suppliersError, suppliersReady,
    locations, locationsError,
    fx, fxLoading, fxError, autoFx, manualRate,
    saving, savingAs, error, costQuestion,
    poNumber,
    suggestionDays, suggestionScope, suggesting, suggestion,
  } = state;

  const {
    setSupplierId, setLocationId, setNeedByDate,
    setNotes, setSupplierNotes, setReferenceNumber,
    setTaxInclusive, setSelectedLines,
    setAutoFx, setManualRate, setError, setSuggestionDays, setSuggestionScope,
    loadFx, applyPickedRows, generateLinesFromSales,
    removeLine, updateLine, save, answerCostQuestion,
  } = actions;

  const {
    selectedSupplier, storeCurrency, supplierCurrencyCode, supplierCurrencySymbol,
    isMismatchedCcy, subtotal, taxTotal, hasTaxedLine, total,
    activeRate, storeTotal, unpricedLineCount, orderRuleLineCount,
    supplierMismatchItems, offerLock, isValid, generateWouldDiscard,
  } = derived;

  // How the merchant wants the lines to arrive: 'blank' (pick items by hand)
  // or 'sales' (the trailing-rate suggestion). Display only — it reveals the
  // suggestion's controls; the lines themselves are the hook's.
  const [linesSource, setLinesSource] = useState('blank');
  const suggestionOutcome = suggestion ? describeSuggestion(suggestion) : null;

  // Generate replaces the lines (the hook's rule). When those lines are the
  // merchant's — picked, loaded from a draft, or edited since the last
  // generation — it asks first. A re-run over its own untouched result does
  // not, so tuning the horizon stays one click per try.
  const { confirm: confirmGenerate, dialogProps: generateConfirmProps } = useConfirmDialog();
  const handleGenerate = useCallback(async () => {
    if (generateWouldDiscard) {
      const ok = await confirmGenerate({
        title: t('purchaseOrders.create.generateReplaceTitle'),
        message: t('purchaseOrders.create.generateReplaceMessage', { count: selectedLines.length }),
        confirmLabel: t('purchaseOrders.create.generateReplaceConfirm'),
      });
      if (!ok) return;
    }
    await generateLinesFromSales();
  }, [generateWouldDiscard, confirmGenerate, selectedLines.length, generateLinesFromSales, t]);

  // From scratch the supplier is the first thing the order needs, and its
  // field sits under the how-to-create toggle, so the cursor goes there once
  // the list is in (the field is disabled until then). The form focuses the
  // control itself: MUI Select's own `autoFocus` left focus on its hidden
  // native input (pinned in PurchaseOrderForm.missingCost, "starts with the
  // cursor on Supplier"). Not in rec / edit mode, where the supplier is fixed.
  const supplierSelectRef = useRef(null);
  // Keyed on the list having arrived, not on its identity: a refetch must not
  // pull the cursor back while the merchant is elsewhere in the form.
  useEffect(() => {
    if (!open || !suppliersReady || recommendationId || purchaseOrderId) return;
    supplierSelectRef.current?.focus();
  }, [open, suppliersReady, recommendationId, purchaseOrderId]);

  // ---------------------------------------------------------------------
  // AG Grid ↔ hook-state sync.
  //
  // AG Grid's `valueSetter` mutates `params.data` in place (that's the
  // contract). But `selectedLines` lives in hook state — if we don't
  // re-sync, a re-render from any other source would wipe the edit.
  //
  // `onCellValueChanged` fires at commit (blur / Enter / Tab / stopEditing)
  // — NOT per keystroke — so calling `updateLine` here is a single state
  // update per commit, and re-render cost is negligible.
  //
  // Ordering contract enforced at Save below:
  //   1. stopEditing() — force any in-flight cell edit to commit
  //   2. AG Grid fires onCellValueChanged synchronously for the committed cell
  //   3. Those calls update hook state via updateLine
  //   4. Save payload is built from the (now-current) hook state
  // ---------------------------------------------------------------------
  const onSelectedLineCellChanged = useCallback((params) => {
    const field = params.colDef?.field;
    if (!field) return;
    // supplier_sku / quantity / cost / tax_rate_percentage — all live on
    // the line. params.data was already mutated by valueSetter; mirror the
    // new value into hook state.
    updateLine(params.data.item_id, { [field]: params.data[field] });
  }, [updateLine]);

  // ── Add items: the supplier-catalog picker ──────────────────────────
  // Lines are added through the shared ItemPickerDialog over the
  // /supplier_catalog/ endpoint, which pages, searches and sorts the
  // supplier's whole catalog server-side. The order's lines open ticked at
  // their own quantity, so the picker edits the whole order: unticking one
  // removes it, and confirming hands every ticked row back.
  //
  // The picker answers "what does the shop need, and how many": it shows
  // inventory, demand and the supplier's order rules, and edits only the
  // quantity. Price, tax and the supplier SKU are the order's commercial
  // terms and are edited on the order lines alone.
  const [pickerOpen, setPickerOpen] = useState(false);
  const canPickItems = !!supplierId && !!locationId;
  const pickerBlockedReason = !supplierId
    ? t('purchaseOrders.create.selectSupplierFirst')
    : !locationId
    ? t('purchaseOrders.create.selectDestination')
    : '';
  const pickerParams = useMemo(() => ({
    supplier_id: supplierId,
    location_id: locationId,
  }), [supplierId, locationId]);
  const pickerSelected = useMemo(
    () => selectedLines.map(pickerRowFromLine),
    [selectedLines],
  );

  // The picker's one editable cell (order qty) is scratch state on the
  // catalog row; `edits` carries what was typed and the merge below hands it
  // to applyPickedRows, which reads the row snapshot once.
  const handlePickerConfirm = useCallback(({ rows, edits }) => {
    applyPickedRows(rows.map((row) => ({ ...row, ...edits[String(row.item_id)] })));
    setPickerOpen(false);
  }, [applyPickedRows]);
  const pickerConfirmLabel = useCallback(() => t('btn.apply'), [t]);

  // ── Manual-rate lock prompt ────────────────────────────────────────
  // When the order converts at a typed rate the supplier does not already
  // carry as its lock (`offerLock`), saving first asks whether to store that
  // rate as the supplier's default; the hook's save does the rest.
  const [lockPromptStatus, setLockPromptStatus] = useState(null);

  // The delivery date field holds no day the order can be issued with
  // (unfinished, or before today): issuing waits, a draft stores no date.
  const [needByDateRefused, setNeedByDateRefused] = useState(false);

  // Wrapped save — preserves the stopEditing() commit pattern, and
  // intercepts to ask about locking the FX rate when relevant.
  const handleSave = useCallback(async (targetStatus) => {
    // Order matters (see comment on onSelectedLineCellChanged above):
    // stopEditing() must fire BEFORE save() so any pending cell edit
    // commits via onCellValueChanged → updateLine → hook state, and
    // save() then reads the up-to-date selectedLines.
    selectedGridRef.current?.api?.stopEditing();
    if (offerLock) {
      setLockPromptStatus(targetStatus);
      return;
    }
    await save(targetStatus);
  }, [offerLock, save]);

  const onLockPromptDecide = useCallback(async (lockRate) => {
    const targetStatus = lockPromptStatus;
    setLockPromptStatus(null);
    await save(targetStatus, { lockRate });
  }, [lockPromptStatus, save]);

  // ---------------------------------------------------------------------
  // Picker columns — appended after the item identity ItemPickerDialog owns.
  // Ordering to a destination the item isn't stocked at is a normal way to
  // seed a location, and receiving stocks it on the channel, so the row
  // carries no warning. Such a row arrives with null on-hand / in-transit /
  // demand, which the numeric columns render blank rather than as a 0 we
  // never counted. Those three are computed per page after the query, so
  // the endpoint cannot sort by them — `sortable: false` keeps the header
  // from offering an order the server would refuse (invalid_sort).
  // ---------------------------------------------------------------------
  const pickerColumns = useMemo(() => [
    // Read-only here: the search matches it, so it is how a merchant holding
    // the supplier's price list finds the item. It is edited on the lines.
    { field: 'supplier_sku', headerName: t('purchaseOrders.create.supplierSku') },
    { field: 'on_hand', headerName: t('purchaseOrders.create.onHand'), type: 'numberColumn', sortable: false },
    { field: 'in_transit', headerName: t('purchaseOrders.create.inTransit'), type: 'numberColumn', sortable: false },
    { field: 'net_demand_30d', headerName: t('purchaseOrders.create.netDemand30d'), type: 'numberColumn', sortable: false },
    {
      field: '_draftQty',
      headerName: t('purchaseOrders.create.quantity'),
      type: 'numberColumn',
      editable: true,
      sortable: false,
      valueSetter: (params) => {
        const val = Math.round(parseNumber(params.newValue));
        if (isNaN(val) || val < 1) return false;
        params.data._draftQty = val;
        return true;
      },
      ...orderRuleWarning(t, formatNumber, (row) => ({ ...row, quantity: row._draftQty })),
    },
  ], [t, parseNumber, formatNumber]);

  // ---------------------------------------------------------------------
  // Selected-lines grid column defs
  // ---------------------------------------------------------------------
  const selectedLinesColumnDefs = useMemo(() => [
    ...itemIdentityColumns(isShopify, {
      productHeader: t('purchaseOrders.create.product'),
      product: flexColumn(2),
      codeHeader: t('purchaseOrders.create.itemCode'),
      code: flexColumn(1, 120),
      nameHeader: t('purchaseOrders.create.itemName'),
      name: flexColumn(2, 180),
    }),
    {
      field: 'supplier_sku',
      headerName: t('purchaseOrders.create.supplierSku'),
      ...flexColumn(),
      minWidth: 120,
      editable: true,
      valueSetter: setSupplierSku,
    },
    ...orderRuleColumns(t),
    {
      field: 'quantity',
      headerName: t('purchaseOrders.create.quantity'),
      type: 'numberColumn',
      editable: true,
      valueSetter: (params) => {
        const val = Math.round(parseNumber(params.newValue));
        if (isNaN(val) || val < 1) return false;
        params.data.quantity = val;
        return true;
      },
      ...orderRuleWarning(t, formatNumber),
    },
    {
      field: 'cost',
      headerName: t('purchaseOrders.create.unitCost')
        + (supplierCurrencyCode ? ` (${supplierCurrencyCode})` : ''),
      type: 'decimalColumn',
      editable: true,
      // Null cost is a genuinely unknown price, not a zero — see
      // lineExtendedTotal. No tint: an unpriced line is a normal state while
      // a quote is pending, and the dash is the whole message.
      valueFormatter: (params) => (params.value == null ? '—' : formatNumber(params.value, 2)),
      valueSetter: (params) => {
        const val = parseNumber(params.newValue);
        if (val === null || isNaN(val) || val < 0) return false;
        params.data.cost = val;
        return true;
      },
    },
    {
      field: 'tax_rate_percentage',
      headerName: t('purchaseOrders.create.taxPct'),
      type: 'decimalColumn',
      editable: true,
      valueFormatter: (params) => params.value == null ? '—' : `${formatNumber(params.value, 2)}%`,
      valueSetter: (params) => {
        if (params.newValue === '' || params.newValue == null || params.newValue === '—') {
          params.data.tax_rate_percentage = null;
          return true;
        }
        const val = parseNumber(params.newValue);
        if (val === null || isNaN(val) || val < 0 || val > 100) return false;
        params.data.tax_rate_percentage = val;
        return true;
      },
    },
    {
      headerName: t('purchaseOrders.create.lineTotal'),
      type: 'decimalColumn',
      valueGetter: (params) => lineExtendedTotal(params.data),
      valueFormatter: (params) => (params.value == null ? '—' : formatNumber(params.value, 2)),
    },
    iconActionColumn({
      cellRenderer: GridIconAction,
      cellRendererParams: {
        icon: Trash,
        label: t('btn.remove'),
        onClick: (row) => removeLine(row.item_id),
      },
    }),
  ], [t, isShopify, supplierCurrencyCode, formatNumber, parseNumber, removeLine]);

  // Title branches by mode: edit > from-rec > from-scratch. In edit mode the PO
  // number is appended once the draft pre-fill resolves — the bare key is shown
  // briefly during the async load so we don't render "— undefined".
  // Proposed-list grid grows with its rows but caps at SELECTED_GRID_MAX_H,
  // past which it scrolls internally. Row height is the theme's ROW_HEIGHT;
  // the header allowance is a slight overestimate
  // so the last row never clips.
  const SELECTED_GRID_MAX_H = 340;
  const selectedGridHeight = Math.min(
    SELECTED_GRID_MAX_H,
    50 + selectedLines.length * ROW_HEIGHT,
  );

  const titleKey = purchaseOrderId
    ? (poNumber ? 'purchaseOrders.create.titleEditWithNumber' : 'purchaseOrders.create.titleEdit')
    : recommendationId
    ? 'purchaseOrders.create.titleFromRec'
    : 'purchaseOrders.create.title';

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      className="dialog-form dialog-form--wide dialog-form--top"
    >
      <DialogTitle>
        {t(titleKey, { poNumber })}
        <IconButton onClick={onClose} size="small" disabled={saving}><X size={ICON.xl} /></IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Withheld save actions need a reason on screen — a dialog whose only
            button is Cancel reads as broken otherwise. */}
        {erpModeUnknown && (
          <Alert severity="error">
            {t('purchaseOrders.create.erpModeUnknown')}
          </Alert>
        )}

        {/* ─────── PO Details ─────── */}
        <Typography className="dialog-form-section">
          {t('purchaseOrders.create.detailsSection')}
        </Typography>

        {/* ─────── Where the lines come from. Blank: the merchant picks items
             (Add items in the Order lines header below). Based on sales: the
             trailing-rate suggestion — the horizon, where sales and stock are
             read from, and Generate, which REPLACES the lines (the hook's
             rule). A toggle, not a select, so both choices read without a
             click. Not in rec mode: those lines are the engine's, sized on
             the forecast, and a trailing-rate replacement would silently
             swap one method for the other. The days field bounds nothing;
             the server refuses an out-of-range horizon
             (invalid_suggest_horizon).

             The suggestion's controls are withheld — not greyed — until the
             supplier and destination are chosen, and the reason takes their
             place: three disabled fields beside a toggle that just responded
             read as a broken form. ─────── */}
        {!recommendationId && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight="var(--font-weight-semibold)">
              {t('purchaseOrders.create.linesSourceLabel')}
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={linesSource}
              onChange={(_e, v) => v && setLinesSource(v)}
              disabled={saving || suggesting}
            >
              <Tooltip title={t('purchaseOrders.create.linesSourceBlankHelp')}>
                <span>
                  <ToggleButton value="blank">{t('purchaseOrders.create.linesSourceBlank')}</ToggleButton>
                </span>
              </Tooltip>
              <Tooltip
                title={t('purchaseOrders.create.linesSourceSalesHelp', { window: SALES_RATE_WINDOW_DAYS })}
              >
                <span>
                  <ToggleButton value="sales">{t('purchaseOrders.create.linesSourceSales')}</ToggleButton>
                </span>
              </Tooltip>
            </ToggleButtonGroup>
            {linesSource === 'sales' && !canPickItems && (
              <Typography variant="body2" color="text.secondary">
                {pickerBlockedReason}
              </Typography>
            )}
            {linesSource === 'sales' && canPickItems && (
              <>
                <TextField
                  size="small"
                  type="number"
                  label={t('purchaseOrders.create.suggestDays')}
                  value={suggestionDays}
                  onChange={e => setSuggestionDays(e.target.value === '' ? '' : Number(e.target.value))}
                  inputProps={{ min: 1, step: 1, inputMode: 'numeric' }}
                  sx={{ width: 150 }}
                  disabled={saving || suggesting}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="po-suggest-scope-label">{t('purchaseOrders.create.suggestScope')}</InputLabel>
                  <Select
                    labelId="po-suggest-scope-label"
                    value={suggestionScope}
                    onChange={e => setSuggestionScope(e.target.value)}
                    label={t('purchaseOrders.create.suggestScope')}
                    disabled={saving || suggesting}
                  >
                    {SUGGEST_SCOPES.map(scope => (
                      <MenuItem key={scope} value={scope}>
                        {scope === 'store'
                          ? t('purchaseOrders.create.suggestScopeStore')
                          : t('purchaseOrders.create.suggestScopeLocation')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title={t('purchaseOrders.create.generateHelp')}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleGenerate}
                      disabled={saving || suggesting}
                    >
                      {t('purchaseOrders.create.generate')}
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ flex: 1, minWidth: 220 }} required>
            <InputLabel id="po-supplier-label">{t('purchaseOrders.create.supplier')}</InputLabel>
            <Select
              labelId="po-supplier-label"
              value={supplierId}
              onChange={e => {
                setSupplierId(e.target.value);
                setSelectedLines([]);
              }}
              label={t('purchaseOrders.create.supplier')}
              disabled={suppliersLoading || !!recommendationId || !!purchaseOrderId}
              inputRef={supplierSelectRef}
            >
              {suppliers.map(s => (
                <MenuItem key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</MenuItem>
              ))}
            </Select>
            {suppliersError && <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>{suppliersError}</Typography>}
            {/* Supplier metadata sits directly under the field it describes —
                payment terms + the supplier's transacting currency. Both chips
                are mounted from the moment the form opens; with no supplier
                chosen they read '—' and stay muted. */}
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
              <StatusChip
                label={t('purchaseOrders.create.paymentTermsChip', {
                  // A supplier may legitimately have no terms (PaymentTerms is
                  // blank-by-default), so both states read the same dash.
                  terms: selectedSupplier?.payment_terms || '—',
                })}
                color="neutral"
              />
              <StatusChip
                label={selectedSupplier
                  ? t(
                    supplierCurrencySymbol
                      ? 'purchaseOrders.create.supplierCcyChipWithSymbol'
                      : 'purchaseOrders.create.supplierCcyChip',
                    { ccy: supplierCurrencyCode, symbol: supplierCurrencySymbol }
                  )
                  : t('purchaseOrders.create.supplierCcyChip', { ccy: '—' })}
                color={selectedSupplier ? 'info' : 'neutral'}
              />
            </Stack>
          </FormControl>

          <FormControl size="small" sx={{ flex: 1, minWidth: 220 }} required>
            <InputLabel id="po-location-label">{t('purchaseOrders.create.location')}</InputLabel>
            <Select
              labelId="po-location-label"
              value={locationId}
              onChange={e => setLocationId(e.target.value)}
              label={t('purchaseOrders.create.location')}
              disabled={!!recommendationId || !!purchaseOrderId}
            >
              {locations.map(l => (
                <MenuItem key={l.location_id} value={l.location_id}>
                  {l.location_name}
                </MenuItem>
              ))}
            </Select>
            {locationsError && <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>{locationsError}</Typography>}
          </FormControl>

          {/* Starts at the date the server would stamp; kept only when the
              order is issued (usePurchaseOrderDraft's needByDate). */}
          <Box>
            <AppDatePicker
              label={t('purchaseOrderDetail.meta.expectedDeliveryDate')}
              value={needByDate}
              onChange={setNeedByDate}
              onRefusedChange={setNeedByDateRefused}
              minDate={storeToday}
              disabled={!supplierId || !locationId || saving}
            />
            {needByDateError && <Typography color="error" variant="caption" component="div" sx={{ mt: 0.5 }}>{needByDateError}</Typography>}
          </Box>
        </Box>

        {/* ─────── FX row (only when cross-ccy) ─────── */}
        {isMismatchedCcy && (
          <Box sx={{
            display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap',
            p: 1.5, borderRadius: 1,
            backgroundColor: 'color-mix(in srgb, var(--color-info) 6%, transparent)',
          }}>
            <Typography variant="body2" fontWeight="var(--font-weight-semibold)" sx={{ minWidth: 120 }}>
              {t('purchaseOrders.create.exchangeRate')}
              {` ${supplierCurrencyCode} → ${storeCurrency}`}
            </Typography>
            <TextField
              size="small"
              value={autoFx && fx?.rate ? formatNumber(parseFloat(fx.rate), 4) : manualRate}
              onChange={e => {
                const v = e.target.value.replace(/[^0-9.,]/g, '');
                setManualRate(v);
                if (autoFx) setAutoFx(false);
              }}
              sx={{ width: 140 }}
              disabled={fxLoading}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoFx}
                  onChange={e => {
                    const next = e.target.checked;
                    setAutoFx(next);
                    // Ticking ON is always an explicit ask for the current
                    // market rate, even when the supplier has a locked
                    // default — refresh=true bypasses the lock at the
                    // resolve_fx endpoint, so merchants can override their
                    // hedge for a single PO without having to clear it on
                    // Supplier Setup. Force-refresh past the daily cron in
                    // case it hasn't fired yet today.
                    if (next) loadFx(true);
                  }}
                  size="small"
                  disabled={fxLoading}
                />
              }
              label={t('purchaseOrders.create.autoFx')}
            />
            {autoFx && fx?.locked && fx?.rate && (
              <StatusChip
                label={t('purchaseOrders.create.fxProvLocked', {
                  rate: formatNumber(parseFloat(fx.rate), 4),
                })}
                color="info"
              />
            )}
            {autoFx && !fx?.locked && fx?.rate_date && (
              <StatusChip
                label={t('purchaseOrders.create.fxProvAuto', {
                  date: formatDate(fx.rate_date),
                })}
                color="success"
              />
            )}
            {!autoFx && (
              <StatusChip
                label={t('purchaseOrders.create.fxProvManual')}
                color="warning"
              />
            )}
            {fxError && (
              <Typography variant="caption" color="error" sx={{ width: '100%' }}>{fxError}</Typography>
            )}
          </Box>
        )}

        {/* ─────── Order lines — the hero of every mode, so it leads. The
             one way to add a line by hand is the Add items button in its
             header, which opens the supplier-catalog picker. The button stays
             visible (disabled, with the reason) until supplier and
             destination are chosen, so the merchant learns where lines come
             from before they can add one. ─────── */}
        <Box
          className="dialog-form-section"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
        >
          <span>
            {t('purchaseOrders.create.linesSection')}
            {selectedLines.length > 0 && ` (${selectedLines.length})`}
          </span>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={pickerBlockedReason}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Plus size={ICON.md} />}
                  onClick={() => setPickerOpen(true)}
                  disabled={!canPickItems || saving}
                >
                  {t('purchaseOrders.create.addItems')}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Box>
        {/* What the last generation concluded, so the lines below read as its
            result and not as something the merchant picked. */}
        {suggestionOutcome && suggestion.lineCount > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {suggestionOutcome.sentences.map(([key, values]) => t(key, values)).join(' ')}
          </Typography>
        )}
        {/* Hard block: items reassigned to another supplier can't be bought on
            this PO (their cost is in that supplier's currency). Save is disabled
            until the merchant removes them — matches the server-side reject. */}
        {supplierMismatchItems.length > 0 && (
          <Alert severity="error">
            {t('purchaseOrders.create.supplierMismatchBanner', {
              count: supplierMismatchItems.length,
              items: supplierMismatchItems.join(', '),
            })}
          </Alert>
        )}
        {/* No banner for an unpriced line or a missing tax rate: the "—" in
            Unit cost and Tax % is the whole message, and neither blocks the
            save. */}
        {/* Names the three rule columns below by their headers, so the
            merchant knows where to read the limit an amber quantity breaks. */}
        {orderRuleLineCount > 0 && (
          <Alert severity="warning">
            {t('purchaseOrders.create.orderRulesBanner', {
              count: orderRuleLineCount,
              moq: t('supplierCatalog.columns.moq'),
              maxOq: t('supplierCatalog.columns.maxOq'),
              orderMultiple: t('supplierCatalog.columns.orderMultiple'),
            })}
          </Alert>
        )}
        {selectedLines.length === 0 ? (
          // From scratch nothing is chosen yet, so the empty state names the
          // next step (the same reason the disabled button's tooltip gives)
          // rather than pointing at a supplier's catalog that has no supplier.
          <Box sx={{ py: 3, textAlign: 'center', border: '1px dashed var(--color-border)', borderRadius: 1 }}>
            {/* An empty generation is a conclusion about the merchant's sales
                and inventory — nothing sold, or everything that sold is
                covered — said here, where the lines would be, not as an
                error. */}
            {suggestionOutcome && suggestion.lineCount === 0 ? (
              <>
                <Typography variant="body2" fontWeight="var(--font-weight-semibold)">
                  {t(suggestionOutcome.title)}
                </Typography>
                {suggestionOutcome.sentences.map(([key, values]) => (
                  <Typography key={key} variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t(key, values)}
                  </Typography>
                ))}
              </>
            ) : (
              // The next step is the mode's: Add items on a blank order,
              // Generate when the lines come from sales.
              <Typography variant="body2" color="text.secondary">
                {!canPickItems
                  ? pickerBlockedReason
                  : linesSource === 'sales'
                  ? t('purchaseOrders.create.noSelectedLinesSales')
                  : t('purchaseOrders.create.noSelectedLines')}
              </Typography>
            )}
          </Box>
        ) : (
          gridTheme && (
            // Grow to content, capped at SELECTED_GRID_MAX_H — short POs no
            // longer reserve a half-empty 340px box; long ones scroll inside.
            <Box sx={{ height: selectedGridHeight, flexShrink: 0 }}>
              <AgGridReact
                ref={selectedGridRef}
                {...defaultGridProps}
                {...editableGridProps}
                theme={gridTheme}
                columnDefs={selectedLinesColumnDefs}
                columnTypes={columnTypes}
                rowData={selectedLines}
                defaultColDef={defaultColDef}
                getRowId={getSelectedLineRowId}
                onCellValueChanged={onSelectedLineCellChanged}
                sideBar={false}
                getContextMenuItems={copyOnlyContextMenuItems}
              />
            </Box>
          )
        )}

        {/* ─────── Totals preview ─────── */}
        {/* While any line is unpriced the order has no total: every money
            figure here is a dash, never the priced lines' sum, which the
            merchant would read as the whole. Same rule as the saved header
            (po_viewset._header_totals) and the PDF. */}
        {selectedLines.length > 0 && (
          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
            gap: 0.25, pt: 1, borderTop: '1px solid var(--color-border)',
          }}>
            <Typography variant="body2" color="text.secondary">
              {t('purchaseOrders.create.subtotal')}:{' '}
              {unpricedLineCount > 0 ? '—'
                : supplierCurrencyCode ? formatCurrency(subtotal, supplierCurrencyCode) : formatNumber(subtotal, 2)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3, width: '100%' }}>
              <Tooltip title={hasTaxedLine ? '' : t('purchaseOrders.create.taxInclusiveNeedsRate')}>
                <span>
                  <FormControlLabel
                    control={<Checkbox checked={taxInclusive} onChange={e => setTaxInclusive(e.target.checked)} size="small" />}
                    label={t('purchaseOrders.create.taxInclusive')}
                    disabled={!hasTaxedLine}
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                  />
                </span>
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
                {taxInclusive
                  ? t('purchaseOrders.create.taxIncluded')
                  : t('purchaseOrders.create.tax')}:{' '}
                {unpricedLineCount > 0 ? '—'
                  : supplierCurrencyCode ? formatCurrency(taxTotal, supplierCurrencyCode) : formatNumber(taxTotal, 2)}
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight="var(--font-weight-semibold)">
              {t('purchaseOrders.create.total')}:{' '}
              {unpricedLineCount > 0 ? '—'
                : supplierCurrencyCode ? formatCurrency(total, supplierCurrencyCode) : formatNumber(total, 2)}
              {isMismatchedCcy && storeTotal != null && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  {` ≈ ${formatCurrency(storeTotal, storeCurrency)} @ ${formatNumber(activeRate, 4)}`}
                </Typography>
              )}
            </Typography>
          </Box>
        )}

        {/* ─────── Notes section (collapsible, below items) ─────── */}
        <Accordion disableGutters sx={{ boxShadow: 'none', border: '1px solid var(--color-border)' }}>
          <AccordionSummary expandIcon={<CaretDown size={ICON.md} />}>
            <Typography variant="body2" fontWeight="var(--font-weight-semibold)">
              {t('purchaseOrders.create.notesSection')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* No client caps: PurchaseOrderCreateSerializer bounds all three
                and refuses over-long text at save, which the banner shows. */}
            <Stack spacing={2}>
              <TextField
                size="small"
                label={t('purchaseOrders.create.referenceNumber')}
                value={referenceNumber}
                onChange={e => setReferenceNumber(e.target.value)}
                helperText={t('purchaseOrders.create.referenceNumberHelper')}
                sx={{ maxWidth: 400 }}
              />
              <TextField
                size="small"
                label={t('purchaseOrders.create.supplierMessage')}
                value={supplierNotes}
                onChange={e => setSupplierNotes(e.target.value)}
                helperText={t('purchaseOrders.create.supplierMessageHelper')}
                multiline
                rows={2}
                fullWidth
              />
              <TextField
                size="small"
                label={t('purchaseOrders.create.internalMemo')}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                helperText={t('purchaseOrders.create.internalMemoHelper')}
                multiline
                rows={2}
                fullWidth
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        <DialogErrorBanner
          error={error}
          onClose={() => setError(null)}
          heading={t('purchaseOrders.create.errorHeading')}
        />
      </DialogContent>

      {/* Cancel always stays, so the merchant is never trapped. The two save
          actions are withheld while the PO's lifecycle owner is unknown:
          offering "Save as draft" to an ERP store is a button the backend
          always rejects, and offering "Create in Exact" to a Manexys store is
          the wrong verb. Draft is additionally hidden while the check is still
          in flight. */}
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={saving}>
          {t('btn.cancel')}
        </Button>
        {poGate.mayWrite && (
          <Button variant="outlined" onClick={() => handleSave('draft')} disabled={!isValid || saving} loading={savingAs === 'draft'}>
            {t('purchaseOrders.create.saveAsDraft')}
          </Button>
        )}
        {!erpModeUnknown && (
          <Button
            variant="contained"
            onClick={() => handleSave('open')}
            disabled={!isValid || saving || erpModePending || needByDateRefused}
            loading={savingAs === 'open'}
          >
            {erpManaged ? t('purchaseOrders.create.inExact') : t('purchaseOrders.create.createPO')}
          </Button>
        )}
      </DialogActions>

      <ConfirmDialog {...generateConfirmProps} />

      {/* ── Lock-rate confirmation prompt ────────────────────────────────
           Appears only when the merchant typed a manual rate that differs
           from the supplier's current lock (or the supplier has none).
           "Just this PO" is the primary/default — the merchant who didn't
           think about persistence isn't punished by an extra click. Mounted
           once a supplier is chosen: the prompt is about that supplier's
           rate, and a mismatched currency (its precondition) needs one. */}
      {selectedSupplier && (
      <Dialog
        className="dialog-form dialog-form--confirm"
        open={lockPromptStatus !== null}
        onClose={() => onLockPromptDecide(false)}
      >
        <DialogTitle>
          {t('purchaseOrders.create.lockRatePrompt.title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('purchaseOrders.create.lockRatePrompt.body', {
              // Display the rate exactly as the merchant typed it. Any
              // parse-then-format roundtrip risks locale ambiguity for
              // "4,20"-style inputs (which the strict parser reads as 420 in
              // US-locale environments).
              rate: manualRate,
              base: supplierCurrencyCode,
              quote: storeCurrency,
              supplier: selectedSupplier.supplier_name,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => onLockPromptDecide(true)} disabled={saving}>
            {t('purchaseOrders.create.lockRatePrompt.lockIt')}
          </Button>
          <Button variant="contained" onClick={() => onLockPromptDecide(false)} disabled={saving} autoFocus>
            {t('purchaseOrders.create.lockRatePrompt.justThisPO')}
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {selectedSupplier && (
        <PoCostQuestionDialog
          question={costQuestion}
          supplierName={selectedSupplier.supplier_name}
          onAnswer={answerCostQuestion}
        />
      )}

      {/* Mounted once a supplier is chosen — the picker is titled by it and
          cannot open before it. Sort/visibility/order on the picker grid
          persist across dialog opens through `gridStateKey`. */}
      {selectedSupplier && (
        <ItemPickerDialog
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          title={t('purchaseOrders.create.addItemsTitle')}
          subtitle={selectedSupplier.supplier_name}
          endpoint="/api/po-calculations/purchase-orders/supplier_catalog/"
          params={pickerParams}
          gridStateKey="po_create_supplier_catalog"
          selectionMode="rows"
          selected={pickerSelected}
          extraColumns={pickerColumns}
          skuField="sku"
          confirmLabel={pickerConfirmLabel}
          onConfirm={handlePickerConfirm}
        />
      )}
    </Dialog>
  );
}

export default PurchaseOrderForm;
